// Which messages one bank's adapter actually speaks.
//
// The Expected Response picker needs this and the Control Command dropdown does
// not: a command an adapter cannot send simply fails to appear, but a RESPONSE
// an adapter cannot report is worse than absent — the action saves cleanly, the
// button dispatches, and the verification window closes on silence every single
// time. "No confirmation, forever" is indistinguishable from a broken
// controller, which is exactly the ambiguity expected-response verification
// exists to remove.
//
// Nothing here fetches. The Actions step already loads an adapter's keys and
// derives its Control options for the command dropdown; this reads those same
// two results, so there is one adapter data path rather than two that can
// disagree about what a bank supports.
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { catalogIdForEnum } from "../../monitoringManagement/Actions/normalizeActionMessages";
import { MSG_TYPE_CONTROL } from "../../../customHooks/useSchemaMessages";

/**
 * The two fields of an adapter key that identify a message.
 *
 * `messageContent` is the `MessageContent` union tag and `messageValue` the
 * member within it — together exactly the (category, member) pair the message
 * catalog is keyed by. Both optional because the payload is untyped JSON at
 * this boundary and a key row can carry neither.
 */
export interface AdapterKeyLike {
  messageContent?: unknown;
  messageValue?: unknown;
}

/** A Control command the step derived for the command dropdown. */
export interface ControlOptionLike {
  numeric?: unknown;
}

/**
 * Catalog ids for every message an adapter declares, deduplicated, in the order
 * first seen.
 *
 * Ids come from `catalogIdForEnum` rather than being composed here, so a change
 * to how the catalog builds its ids cannot leave this producing keys that match
 * nothing — the failure mode that would silently empty the picker.
 *
 * The `controlOptions` half is not redundant with the keys half. MODBUS, OPCUA,
 * LOBBYVISION2 and SEQUENT adapters keep their control commands ONLY in a
 * protocol-specific binary CDO and have no `keys` rows at all, so a bank on one
 * of those protocols contributes nothing through `adapterKeys` and would come
 * back empty.
 *
 * EMPTY is a real answer meaning "this adapter declared nothing we could map",
 * NOT "this adapter has no messages". A caller must read it as "do not
 * restrict" and say so on screen — see `SelectMessagesMultiple.allowedIds`.
 */
export function adapterDeclaredMessageIds(
  adapterKeys: readonly AdapterKeyLike[],
  controlOptions: readonly ControlOptionLike[] = [],
): string[] {
  const ids = new Set<string>();

  adapterKeys.forEach((key) => {
    if (typeof key?.messageContent !== "number" || typeof key?.messageValue !== "number") return;
    // NONE is the union's absent tag, not a category, and it maps to no catalog
    // entry. Skipping it explicitly keeps a half-populated key row from being
    // read as "a STATUS message numbered <messageValue>".
    if (key.messageContent === MessageContent.NONE) return;
    const catalogId = catalogIdForEnum(key.messageContent, key.messageValue);
    if (catalogId) ids.add(catalogId);
  });

  controlOptions.forEach((option) => {
    // `typeof === "number"` and never truthiness: Control.CarCallLock is a
    // legitimate 0, and a `!option.numeric` guard would drop exactly it.
    if (typeof option?.numeric !== "number") return;
    const catalogId = catalogIdForEnum(MSG_TYPE_CONTROL, option.numeric);
    if (catalogId) ids.add(catalogId);
  });

  return [...ids];
}
